import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';
import { LoadManager, StepInfoManager } from '@theme/custom-collection-utility';

export class CustomCollectionFlowComponent extends Component {
  connectedCallback() {
    try {
      super.connectedCallback();

      // Get data from the component element itself since data attributes are on the component
      this.collectionHandle = this.getAttribute('data-collection-handle') || 'maaltijden';
      this.stepsElements = this.querySelectorAll('.step-wrapper');
      this.stepsCount = parseInt(this.getAttribute('data-steps-count') || '1');
      this.stepInfo = this.#initializeStepInfo();

      document.addEventListener('flow-state-changed', /** @type {EventListener} */ (this.#updateFlowVisualState));
      document.addEventListener('sync-quick-filters', this.#debouncedPostRender);

      this.stepsElements.forEach((step) => {
        step.addEventListener('click', this.#handleStepClick);
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  disconnectedCallback() {
    // Clean up event listener
    document.removeEventListener('flow-state-changed', /** @type {EventListener} */ (this.#updateFlowVisualState));
    document.removeEventListener('sync-quick-filters', this.#debouncedPostRender);
    this.#debouncedPostRender?.cancel?.();

    this.stepsElements?.forEach((step) => {
      step.removeEventListener('click', this.#handleStepClick);
    });

    super.disconnectedCallback();
  }

  /**
   * Initializes step info from session storage or creates it if not present
   * @returns {Object} The step info object
   */
  #initializeStepInfo() {
    try {
      /** @type {any} */ 
      const initialStepInfo = StepInfoManager.defaultStepInfo(this.collectionHandle || '', this.stepsCount || 1);
  
      // Delay the update request to ensure other components are initialized
      // setTimeout(() => {
        // Dispatch the flow state changed event
        this.#updateFlowVisualState(StepInfoManager.createFlowStateChangedEvent(initialStepInfo), true);
      // }, 300);

      return initialStepInfo;
    } catch (error) {
      return {};
    }
  }

  updateFlowStepWithProductFiltering = () => {
    try {
      let stepInfo = StepInfoManager.getStepInfo() || null;
      stepInfo = StepInfoManager.updateStepInfo({ 'step-count': 1 });
      // Dispatch the flow state changed event
      this.#updateFlowVisualState(StepInfoManager.createFlowStateChangedEvent(stepInfo), true);
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  // Debounced post-render handler using shared utility
  #debouncedPostRender = debounce(() => {
    try {
      const stepInfo = StepInfoManager.getStepInfo() || null;
      if (!stepInfo || !this.stepsElements?.length) return;
      const currentIndex = (/** @type {any} */ (stepInfo)['step-count'] || 1) - 1;
      const stepType = this.stepsElements[currentIndex]?.getAttribute('data-step-type') || '';
      
      // if (stepType) StepInfoManager.updateStepHeadings(stepType);
    } catch (error) {
      console.error(error);
    }
  }, 100);

  static SEO_HEADING_HIDDEN_CLASS = 'collection-seo-heading--hidden';

  /** @type {HTMLElement | null} */
  _seoHeadingSection = null;

  /**
   * Finds the collection SEO H1 section above main-collection.
   * Shopify wraps JSON sections as `shopify-section-template--{id}__{key}` (not
   * `shopify-section-main`), and template keys differ (section_seo_title vs section_yD34XG).
   * Locate main via results-list, then walk previous siblings for the first section with an h1.
   * @returns {HTMLElement | null}
   */
  #getCollectionSeoHeadingSection() {
    if (this._seoHeadingSection) return this._seoHeadingSection;

    const resultsList = document.querySelector('results-list');
    const mainFromResults = resultsList instanceof HTMLElement ? resultsList.closest('.shopify-section') : null;
    const mainFromSuffix = document.querySelector('.shopify-section[id$="__main"]');
    const main = mainFromResults instanceof HTMLElement
      ? mainFromResults
      : mainFromSuffix instanceof HTMLElement
        ? mainFromSuffix
        : null;

    let el = main?.previousElementSibling ?? null;

    while (el instanceof HTMLElement) {
      if (el.classList.contains('shopify-section') && el.querySelector('h1')) {
        this._seoHeadingSection = el;
        return el;
      }
      el = el.previousElementSibling;
    }

    return null;
  }

  /**
   * Shows the SEO H1 on step 1 and hides the whole section on later steps.
   * @param {number} stepCount
   */
  #syncCollectionSeoHeadingVisibility(stepCount) {
    const seoSection = this.#getCollectionSeoHeadingSection();
    if (!seoSection) return;

    const isFirstStep = Number(stepCount) === 1;
    seoSection.classList.toggle(
      CustomCollectionFlowComponent.SEO_HEADING_HIDDEN_CLASS,
      !isFirstStep
    );
  }

  /**
   * @param {CustomEvent} [event]
   * @param {boolean} [sendUpdateRequest]
   */
  #updateFlowVisualState = (event, sendUpdateRequest = false) => {
    try {
      LoadManager.addLoader();
      this.stepInfo = StepInfoManager.getStepInfo() || null;
      const stepInfoAny = /** @type {any} */ (this.stepInfo);
      const stepCount = stepInfoAny?.['step-count'] || 1;

      this.#syncCollectionSeoHeadingVisibility(stepCount);

      if(this.stepsElements && this.stepInfo) {
        this.stepsElements.forEach((step, index) => {
          if(this.stepInfo && index < (stepInfoAny['step-count'] || 1)) {
            step.classList.add('active');
            step.querySelector('.step-progress-bar')?.classList.add('active');
          }
          else {
            step.classList.remove('active');
            step.querySelector('.step-progress-bar')?.classList.remove('active');
          }
        });

        if(sendUpdateRequest) {
          StepInfoManager.dispatchFlowVisualUpdateRequested(this.stepInfo);
        }
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
    finally {
      LoadManager.removeLoader();
    }
  }

  /** @param {Event} event */
  #handleStepClick = (event) => {
    try {
      const step = /** @type {HTMLElement} */ (event.currentTarget);
      if(step && step.classList.contains('active')) {
        const stepIndex = this.stepsElements ? Array.from(this.stepsElements).indexOf(/** @type {Element} */ (step)) : -1;
        
        if (this.stepInfo && stepIndex >= 0) {
          this.stepInfo = StepInfoManager.updateStepInfo({ 'step-count': stepIndex + 1 });
          // Dispatch the flow state changed event
          this.#updateFlowVisualState(StepInfoManager.createFlowStateChangedEvent(this.stepInfo), true);
        }
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  }
}

if (!customElements.get('custom-collection-flow-component')) {
  customElements.define('custom-collection-flow-component', CustomCollectionFlowComponent);
}